/**
 * How a county's health reads on the operator console.
 *
 * Pure functions with tests, the same treatment `route.ts`, `views.ts`,
 * `reportStatus.ts` and `alerts.ts` get — because the wording *is* the decision
 * here. The console exists because of a morning when a county held no rows and
 * nothing said so, and the one thing it must never do is let that state read as
 * "fine".
 *
 * There are five states and they are genuinely different:
 *
 * - **pulling** — a pull is in flight over a county we already hold.
 * - **never** — we have no record of a successful pull *of this county*. Not
 *   "stale": stale means old data, and this is no data. This is the state Lee
 *   shipped in on 14 Aug 2026. It **outranks a pull in flight**: a first-ever
 *   pull is labelled "Pulling · first time" but stays in this state, because
 *   `verdict` counts states and a county with nothing behind it must count as
 *   a problem while it fills.
 * - **failed** — the last logged run for this county did not succeed. The rows
 *   on screen are the previous copy, kept deliberately.
 * - **stale** — pulled, but longer ago than the refresh interval.
 * - **current** — pulled within the interval.
 *
 * `failed` outranks `stale` because a county whose feed is down can look fresh
 * for hours after the run that failed, and the failure is the actionable fact.
 */

import type { CountyHealth } from "../api/types";

export type HealthState = "pulling" | "never" | "failed" | "stale" | "current";

export interface Health {
  state: HealthState;
  /** Short label for the status cell. */
  label: string;
  /** One sentence saying what it means, shown beneath. */
  detail: string;
  /** Class suffix for the badge: b-red, b-amber, b-grey. */
  tone: "b-red" | "b-amber" | "b-grey";
}

export function health(county: CountyHealth): Health {
  // "We have never held this county" is not undone by a pull being in flight.
  //
  // This used to return a plain `pulling` for any running county, checked
  // first — so a county with no successful pull on record read as "Pulling"
  // and `verdict` counted it among the healthy. Production said
  // **"All 4 counties current; 1 pulling now"** over a Miami-Dade row whose
  // last success was "—", which is the exact false claim this page exists to
  // prevent. The state stays `never`; only the label says a pull is running.
  const neverHeld = !county.finished_at;

  if (county.running) {
    return {
      state: neverHeld ? "never" : "pulling",
      label: neverHeld ? "Pulling · first time" : "Pulling",
      detail: county.progress || "In progress.",
      tone: neverHeld ? "b-amber" : "b-grey",
    };
  }

  // Checked before staleness, and before the last run's outcome: a county with
  // no successful pull on record has nothing behind it, and calling that
  // "stale" would describe old data where there is none at all.
  if (neverHeld) {
    return {
      state: "never",
      label: "Never pulled",
      detail:
        "No successful pull on record for this county. It holds whatever is " +
        "on screen, which may be nothing.",
      tone: "b-red",
    };
  }

  if (county.last_run_ok === false) {
    return {
      state: "failed",
      label: "Last run failed",
      detail:
        "The previous copy is being served. A county that fails is skipped " +
        "rather than emptied.",
      tone: "b-red",
    };
  }

  if (county.stale) {
    return {
      state: "stale",
      label: "Stale",
      detail: "Older than the refresh interval.",
      tone: "b-amber",
    };
  }

  return { state: "current", label: "Current", detail: "", tone: "b-grey" };
}

/**
 * "4 minutes ago", "3 days ago", or "" when there is no timestamp.
 *
 * Empty rather than "never" or "unknown": the caller has a state for that and
 * a second, differently worded one here would be a second answer to the same
 * question.
 */
export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";

  const seconds = Math.round((now - then) / 1000);
  if (seconds < 60) return "just now";

  const units: [number, string][] = [
    [60, "minute"],
    [60, "hour"],
    [24, "day"],
  ];
  let value = seconds;
  let unit = "second";
  for (const [size, name] of units) {
    if (value < size) break;
    value = Math.floor(value / size);
    unit = name;
  }
  return `${value} ${unit}${value === 1 ? "" : "s"} ago`;
}

/**
 * The one-line verdict above the county table.
 *
 * Reports the worst state present, because that is the only summary a console
 * can honestly give: "3 of 4 current" reads as healthy on the morning the
 * fourth one is empty.
 */
export function verdict(counties: CountyHealth[]): string {
  if (counties.length === 0) return "No counties are live.";

  const states = counties.map((county) => health(county).state);
  const count = (state: HealthState) =>
    states.filter((s) => s === state).length;

  const never = count("never");
  const failed = count("failed");
  const stale = count("stale");
  // Not a state: the copy here is the one the engine kept, and may well be
  // current. But a county answering far smaller every morning is the thing
  // this page must not let pass as fine.
  const empty = counties.filter(answeredNothing).length;
  const refused =
    counties.filter((county) => county.engine_refusal).length - empty;
  // Counted off `running` rather than off the state, because a county being
  // pulled for the first time is reported as a problem *and* as in flight —
  // the two are not alternatives, and the state can only hold one of them.
  const pulling = counties.filter((county) => county.running).length;

  const problems: string[] = [];
  if (never) problems.push(`${never} never pulled`);
  if (failed) problems.push(`${failed} failed on the last run`);
  if (stale) problems.push(`${stale} stale`);
  if (refused) problems.push(`${refused} answered far smaller, copy kept`);
  if (empty) problems.push(`${empty} answered nothing (the county's outage), copy kept`);
  const inFlight = pulling ? `; ${pulling} pulling now` : "";

  if (problems.length === 0) {
    return `All ${counties.length} counties current${inFlight}.`;
  }
  return `${problems.join(", ")} of ${counties.length}${inFlight}.`;
}


/**
 * What to say about a county's dated feeds, when there is anything to say.
 *
 * **A frozen feed passes every other check on this page.** The county pulls
 * fine, the rows arrive, the timestamp is minutes old — and Fort Lauderdale's
 * case register, which is the whole of Broward's enforcement coverage, has not
 * gained a case since October 2019 with 4,239 still marked open. "Current" is
 * true of the pull and false of the data, and until the refresh started dating
 * these feeds there was nowhere that difference could show.
 *
 * Exception-only: a feed being written to needs no line, because the county row
 * already says the pull is current. What must never be silent is a feed that
 * stopped, or one we could not date.
 *
 * The two are worded apart on purpose. **Unmeasured is not an accusation** — a
 * service that refused to sort, or was down when we asked, is fine as far as we
 * know, and calling it frozen is the same false claim as calling a dead feed
 * current, pointed the other way.
 */
export interface FeedNote {
  label: string;
  text: string;
  tone: "err" | "sub";
}

export function feedNotes(county: CountyHealth): FeedNote[] {
  const notes: FeedNote[] = [];
  for (const feed of county.feeds ?? []) {
    if (feed.stale && feed.newest) {
      notes.push({
        label: feed.label,
        text:
          `${feed.label}: nothing written since ${feed.newest}` +
          (feed.age_days === null
            ? ""
            : `, ${feed.age_days.toLocaleString()} days ago`),
        tone: "err",
      });
    } else if (!feed.newest) {
      notes.push({
        label: feed.label,
        text:
          `${feed.label}: could not be dated` +
          (feed.note ? ` — ${feed.note}` : ""),
        tone: "sub",
      });
    }
  }
  return notes;
}

/**
 * Whether the refused pull answered nothing at all. Osceola, 30 Sep 2026: the
 * county emptied its parcel layer and answered 0 / 0 for days. That is never
 * a county that got smaller, so there is nothing for the operator to accept —
 * accepting it would delete the county, and the engine refuses it anyway.
 */
export function answeredNothing(county: CountyHealth): boolean {
  const refusal = county.engine_refusal;
  return Boolean(refusal) && !(refusal?.units ?? 0) && !(refusal?.buildings ?? 0);
}

/**
 * What to say under a county whose latest pull the engine refused.
 *
 * Both sets of numbers, because they are the whole decision: Osceola's
 * 2,603 units against 6,231 held was a broken county layer (27 Sep 2026);
 * Okaloosa's 180 buildings against 602 was our own fix (25 Sep). Only the
 * operator can tell them apart, and only with the numbers side by side.
 */
export function refusalNote(county: CountyHealth): string {
  const refusal = county.engine_refusal;
  if (!refusal) return "";
  if (answeredNothing(county)) {
    const held = `${refusal.held_units.toLocaleString()} units / ` +
      `${refusal.held_buildings.toLocaleString()} buildings`;
    return (
      "The county's own source answered nothing at all. That is an outage " +
      `on the county's side, not a smaller county: the ${held} held stay, ` +
      "and the engine tries again at its next check."
    );
  }
  const answered = `${(refusal.units ?? 0).toLocaleString()} units / ` +
    `${(refusal.buildings ?? 0).toLocaleString()} buildings`;
  const held = `${refusal.held_units.toLocaleString()} / ` +
    `${refusal.held_buildings.toLocaleString()}`;
  return (
    `The engine's latest pull answered ${answered} against ${held} held, ` +
    "so it kept the held copy and will try again at its next check."
  );
}
